import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { X, FileText, Save, RotateCcw } from "lucide-react";
import { ensureScripts } from "../reports/legacy/docs";
import { HtmlPasteBox, isHtml, cleanEmailHtml } from "../HtmlPasteBox";

const textToHtml = (t) => (isHtml(t) ? t : String(t || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br/>"));

// Forms_Scripts — the client-facing wording printed / emailed on each document, grouped by document.
const DOCS = ["Spa Quote", "W/O Quote", "Invoice", "Statement", "Delivery Docket", "POS Receipt", "Emails"];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const norm = (s) => String(s || "").replace(/\r\n/g, "\n");

function ScriptCard({ s, onSaved }) {
  const [val, setVal] = useState(norm(s.content));
  const [busy, setBusy] = useState(false);
  useEffect(() => setVal(norm(s.content)), [s.content]);
  const dirty = val !== norm(s.content);
  const save = async (content) => {
    setBusy(true);
    try { await api("PUT", `/scripts/${encodeURIComponent(s.id)}`, { content }); await ensureScripts(); toast.success(`${s.id} saved`); onSaved(s.id, content); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-slate-300 bg-white p-3" data-testid={`script-card-${slug(s.id)}`}>
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div>
          <div className="text-[13px] font-bold text-slate-800">{s.id}</div>
          <div className="text-[11px] text-slate-500">{s.where}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {norm(s.default) !== val && <button disabled={busy} onClick={() => setVal(norm(s.default))} data-testid={`script-reset-${slug(s.id)}`} title="Put the original Spa Buddy wording back (then Save)"
            className="inline-flex items-center gap-1 rounded-sm border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"><RotateCcw className="h-3 w-3" /> Default</button>}
          <button disabled={!dirty || busy} onClick={() => save(val)} data-testid={`script-save-${slug(s.id)}`}
            className="inline-flex items-center gap-1 rounded-sm bg-[#2a9fd6] px-2.5 py-1 text-[11px] font-bold text-white hover:bg-[#1f8cc0] disabled:opacity-40"><Save className="h-3 w-3" /> Save</button>
        </div>
      </div>
      {s.document === "Emails"
        ? <HtmlPasteBox value={textToHtml(val)} onChange={(v) => setVal(v === cleanEmailHtml(textToHtml(norm(s.content))) ? norm(s.content) : v)} testid={`script-text-${slug(s.id)}`} placeholder="Type, or paste a formatted email from Outlook (logos and links are kept)" minH="min-h-[90px]" showClear={false} />
        : <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={Math.min(9, Math.max(2, Math.ceil(val.length / 95) + (val.match(/\n/g) || []).length))}
        data-testid={`script-text-${slug(s.id)}`}
        className={`w-full rounded-sm border px-2 py-1.5 text-[12.5px] leading-snug text-slate-900 outline-none focus:ring-1 focus:ring-[#2a9fd6] ${dirty ? "border-amber-400 bg-amber-50/40" : "border-slate-300"}`} />}
      <div className="mt-1 flex justify-between text-[10.5px] text-slate-400">
        <span>{dirty ? <b className="text-amber-600">Unsaved changes</b> : "Saved"}</span><span>{val.length} chars</span>
      </div>
    </div>
  );
}

export default function DocumentScripts({ onClose }) {
  const [rows, setRows] = useState([]);
  const [doc, setDoc] = useState(DOCS[0]);
  useEffect(() => { api("GET", "/scripts").then(setRows).catch((e) => toast.error(e.message)); }, []);
  const list = useMemo(() => rows.filter((r) => r.document === doc), [rows, doc]);
  const onSaved = (id, content) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, content } : r)));
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/55 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="overlay-document-scripts">
      <div className="relative w-full max-w-4xl my-2 rounded-lg overflow-hidden shadow-2xl border border-sky-300 bg-gradient-to-b from-sky-100 to-sky-200">
        <div className="flex items-center justify-between bg-[#2a9fd6] text-white px-3 py-1.5">
          <div className="flex items-center gap-2 text-[13px] font-semibold"><FileText className="h-4 w-4" /> Scripts — Client Documents</div>
          <button onClick={onClose} data-testid="scripts-close-x" className="h-6 w-6 rounded-sm hover:bg-white/20 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-col sm:flex-row min-h-[28rem]">
          <nav className="sm:w-44 shrink-0 flex sm:flex-col gap-1 p-3 overflow-x-auto border-b sm:border-b-0 sm:border-r border-sky-300 bg-white/40">
            {DOCS.map((d) => (
              <button key={d} onClick={() => setDoc(d)} data-testid={`scripts-doc-${slug(d)}`}
                className={`text-left whitespace-nowrap rounded-sm px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors ${doc === d ? "bg-[#2a9fd6] text-white" : "text-slate-700 hover:bg-white/70"}`}>
                {d} <span className="opacity-60 text-[11px]">({rows.filter((r) => r.document === d).length})</span>
              </button>
            ))}
          </nav>
          <div className="flex-1 p-4 space-y-3" data-testid="scripts-list">
            <p className="text-[11.5px] text-slate-600">Wording your clients see on the <b>{doc}</b>. Placeholders: <code>{"{Company}"}</code>, <code>{"{ClientName}"}</code>, <code>{"{Number}"}</code>{doc === "Emails" && <>, <code>{"{Contact}"}</code>, <code>{"{Attachment}"}</code>, <code>{"{Employee}"}</code>, <code>{"{Address}"}</code>, <code>{"{Suburb}"}</code>, <code>{"{State}"}</code>, <code>{"{Postcode}"}</code>, <code>{"{Phone}"}</code>, <code>{"{Fax}"}</code>, <code>{"{Website}"}</code>, <code>{"{TradingHours}"}</code>, <code>{"{EmailFooter}"}</code></>}. Water test reports and marketing emails are edited elsewhere.</p>
            {list.map((s) => <ScriptCard key={s.id} s={s} onSaved={onSaved} />)}
            {!rows.length && <p className="text-sm text-slate-500">Loading…</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
